import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '@app/services/api';
import '@modules/database/work-items/frontend/components/IssuePickerInput/IssuePickerInput.css';

function IssuePickerInput({ value, onChange, onSelect, currentIssueKey, disabled, placeholder, autoFocus, onKeyDown, issueTypes, project, displayMode, excludeKeys, currentJQL, expression, anchorKey, candidateRole = 'child' }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Search function — called on focus and on value change
  const doSearch = useCallback(async (query) => {
    setLoading(true);
    try {
      const data = await api.searchIssuePicker(query || '', currentIssueKey, project, issueTypes, currentJQL);
      let items = (data?.issues || []).map(issue => ({
        key: issue.key,
        summary: issue.summary || '',
        iconUrl: issue.iconUrl || null,
        issueTypeName: issue.issueTypeName || '',
        statusName: issue.statusName || '',
        statusCategoryKey: issue.statusCategoryKey || '',
      }));
      if (excludeKeys?.length > 0) {
        const excludeSet = new Set(excludeKeys);
        items = items.filter(item => !excludeSet.has(item.key));
      }
      // Expression post-filter: ask Jira whether the per-edge predicate
      // passes for each candidate, hide the failures. Fail-open on
      // error — the submit-time gate is the source of truth.
      // `candidateRole` selects which side of the edge the candidates
      // play: 'child' (anchor is the parent) or 'parent' (anchor is the
      // child being attached to each candidate-as-parent).
      setSuggestions(items);
      setOpen(items.length > 0);
      setHighlightIndex(-1);
    } catch {
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [currentIssueKey, project, issueTypes, excludeKeys, currentJQL, expression, anchorKey, candidateRole]);

  // Debounced search on value change (while focused)
  useEffect(() => {
    if (!focused) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      doSearch(value?.trim() || '');
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, focused, doSearch]);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // On focus — immediately fire a search (shows recent/suggested issues)
  const handleFocus = useCallback(() => {
    setFocused(true);
    doSearch(value?.trim() || '');
  }, [value, doSearch]);

  const selectItem = useCallback((item) => {
    if (onSelect) onSelect(item.key, item);
    else if (onChange) onChange({ target: { value: item.key } });
    setOpen(false);
  }, [onSelect, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (open && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex(i => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
          selectItem(suggestions[highlightIndex]);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
    }
    // Pass through to parent onKeyDown when dropdown is closed
    if (onKeyDown) onKeyDown(e);
  }, [open, suggestions, highlightIndex, selectItem, onKeyDown]);

  return (
    <div className="issue-picker-wrapper" ref={wrapperRef}>
      <div className="issue-picker-input-row">
        <input
          ref={inputRef}
          type="text"
          className="screen-field-input"
          placeholder={placeholder || 'e.g. PROJ-123'}
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        {loading && <span className="issue-picker-spinner screen-spinner" />}
      </div>
      {open && suggestions.length > 0 && (
        <div className="issue-picker-dropdown">
          {suggestions.map((item, idx) => (
            <div
              key={item.key}
              className={`issue-picker-option${idx === highlightIndex ? ' highlighted' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); selectItem(item); }}
              onMouseEnter={() => setHighlightIndex(idx)}
            >
              {!displayMode && item.iconUrl && (
                <img className="linked-issue-type-icon" src={api.getJiraImageUrl(item.iconUrl)} alt="" width="16" height="16" />
              )}
              {displayMode !== 'summary' && <span className="issue-picker-option-key">{item.key}</span>}
              {displayMode !== 'key' && <span className="issue-picker-option-summary">{item.summary}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default IssuePickerInput;
